/**
 * Version Check Utility
 * Détecte les nouvelles versions de l'application et notifie l'utilisateur
 */

/* global __APP_BUILD_VERSION__ */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Vérifier toutes les 5 minutes
let checkInterval = null;
let onVisibilityChange = null;

/**
 * Version du bundle actuellement exécuté, injectée au build depuis
 * public/meta.json (cf. craco.config.js). `null` en développement, où le
 * contrôle de version est de toute façon désactivé (cf. src/App.js).
 */
const RUNNING_VERSION =
  typeof __APP_BUILD_VERSION__ === 'string' ? __APP_BUILD_VERSION__ : null;

/**
 * Récupère la version actuelle depuis le fichier meta.json
 */
const fetchVersion = async () => {
  try {
    const response = await fetch(`/meta.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.version;
  } catch (error) {
    console.warn('Version check failed:', error);
    return null;
  }
};

/**
 * Démarre la vérification périodique des versions
 * @param {Function} onNewVersion - Callback appelé quand une nouvelle version est détectée
 */
export const startVersionCheck = (onNewVersion) => {
  // Version de référence connue : d'abord celle du bundle en cours d'exécution,
  // puis la plus récente déjà signalée à l'utilisateur.
  //
  // ⚠️ Cette référence DOIT être la version réellement exécutée, pas la première
  // valeur renvoyée par le serveur. `index.html` peut être restitué par le cache
  // HTTP du navigateur après une mise en production : l'utilisateur exécute alors
  // l'ancien bundle pendant que /meta.json (requêté en no-store) renvoie déjà la
  // nouvelle version. En partant de la version servie, on enregistrait la nouvelle
  // comme référence et l'écart n'était jamais détecté — l'utilisateur restait
  // silencieusement sur du code périmé.
  let knownVersion = RUNNING_VERSION;

  // Tracé au démarrage : permet de diagnostiquer un onglet resté sur un bundle
  // périmé en comparant simplement avec /meta.json.
  console.log(`App version (bundle): ${RUNNING_VERSION || 'inconnue'}`);

  const checkVersion = async () => {
    const latestVersion = await fetchVersion();

    if (!latestVersion) return;

    // Filet de sécurité : si la version du bundle n'a pas pu être injectée au
    // build, on retombe sur l'ancien comportement — la première valeur servie
    // fait référence, sans rien signaler.
    if (knownVersion === null) {
      knownVersion = latestVersion;
      console.log(`App version: ${knownVersion} (version du bundle inconnue)`);
      return;
    }

    // Version inchangée : rien à faire.
    if (knownVersion === latestVersion) return;

    // Nouvelle version détectée. On avance la référence AVANT de notifier
    // pour ne la signaler qu'une seule fois : sinon chaque tick (toutes les
    // 5 min) re-déclencherait le bandeau tant que l'utilisateur n'a pas
    // rechargé — y compris juste après un « Ignorer » (bug du pop en boucle).
    // Une prochaine mise en prod (version encore différente) re-notifiera une fois.
    console.log(`New version available: ${latestVersion} (current: ${knownVersion})`);
    knownVersion = latestVersion;
    if (onNewVersion) {
      onNewVersion(latestVersion);
    }
  };

  // Vérification initiale
  checkVersion();

  // Vérifications périodiques
  checkInterval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

  // Vérifier aussi dès que l'onglet redevient visible : les utilisateurs
  // laissent l'app ouverte des heures, on évite d'attendre jusqu'à 5 min.
  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkVersion();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
};

/**
 * Arrête la vérification des versions
 */
export const stopVersionCheck = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
  if (onVisibilityChange) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    onVisibilityChange = null;
  }
};

/**
 * Force le rafraîchissement de l'application.
 *
 * ⚠️ `window.location.reload(true)` ne force RIEN : l'argument est ignoré par
 * tous les navigateurs modernes, et un reload ordinaire peut resservir
 * l'`index.html` du cache HTTP — donc les anciens chunks. Il faut d'abord
 * revalider `index.html` avec `cache: 'reload'`, ce qui remplace l'entrée en
 * cache, avant de recharger.
 *
 * Le vidage du Cache API est conservé par sécurité (aucun service worker n'est
 * enregistré aujourd'hui, mais l'appel est inoffensif s'il en apparaissait un).
 */
export const forceRefresh = async () => {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.warn('Cache clearing failed:', error);
  }

  try {
    await fetch(window.location.href, {
      cache: 'reload',
      credentials: 'same-origin'
    });
  } catch (error) {
    console.warn('index.html revalidation failed:', error);
  }

  window.location.reload();
};
